import React, { useState, useEffect, useCallback, useRef } from 'react'
import { Prompt, useHistory, useLocation } from 'react-router-dom'
import Grid from '@mui/material/Grid'
import Paper from '@mui/material/Paper'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import TextField from '@mui/material/TextField'
import FormControl from '@mui/material/FormControl'
import FormControlLabel from '@mui/material/FormControlLabel'
import Radio from '@mui/material/Radio'
import Typography from '@mui/material/Typography'
import Add from '@mui/icons-material/Add'

import Title from '../../component/Title'
import API from '../../component/Axios'
import Snackbars from '../../component/Snackbars'
import Spiner from '../../component/Spiner'
import BottomButtonGroup, { FORM_WIDE_UPLOAD_BUTTON_SX } from '../../component/BottomButtonGroup'
import ListEmptyState from '../../component/ListEmptyState'
import { FORM_IMAGE_PREVIEW_FRAME_SX, FORM_PANEL_SX, getFormChoiceCardSx } from '../../component/formSurfaceStyles'
import { getLocationState } from '../../utils/listPageState'
import {
    applyAppRankingBannerDraft,
    applyAppRankingBannerFieldChange,
    APP_RANKING_BANNER_EXPOSE_REGION_OPTIONS,
    APP_RANKING_BANNER_EXPOSE_REGIONS,
    APP_RANKING_BANNER_IMAGE_FILE_ACCEPT,
    APP_RANKING_BANNER_IMAGE_FILE_ERROR_MESSAGE,
    APP_RANKING_BANNER_IMAGE_SECTION_CONFIGS,
    clearAppRankingBannerDraft,
    createAppRankingBannerDraftPayload,
    createDefaultAppRankingBannerParams,
    createEditAppRankingBannerDraftKey,
    handleAppRankingBannerBackNavigation,
    handleAppRankingBannerImageUpload,
    moveToAppRankingBannerList,
    prepareAppRankingBannerSubmit,
    readAppRankingBannerDraft,
    saveAppRankingBannerDraft,
} from './appRankingBannerDraftUtils'
import AppRankingBannerRewardFields from './AppRankingBannerRewardFields'
import {
    APP_RANKING_BANNER_IMAGE_LEAVE_MESSAGE,
    hasPendingAppRankingBannerImage,
    usePendingAppRankingBannerImageUnloadGuard,
} from './appRankingBannerLeaveGuard'

const PANEL_SX = FORM_PANEL_SX

const IMAGE_PREVIEW_FRAME_SX = {
    ...FORM_IMAGE_PREVIEW_FRAME_SX,
    width: '100%',
    height: { xs: 220, md: 280 },
    mb: 1.5,
}

const IMAGE_PREVIEW_SX = {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
    display: 'block',
}

const getChoiceCardSx = (selected, disabled = false) => ({
    ...getFormChoiceCardSx(selected, disabled),
    p: 1.5,
    width: '100%',
})

const RANKING_BANNER_TYPE_OPTIONS = [
    {
        value: 1,
        title: '러닝 주간',
        description: '주간 러닝 랭킹 기준으로 경품을 노출합니다.',
    },
    {
        value: 2,
        title: '러닝 월간',
        description: '월간 러닝 랭킹 기준으로 경품을 노출합니다.',
    },
    {
        value: 3,
        title: '만보기 주간',
        description: '주간 만보기 랭킹 기준으로 경품을 노출합니다.',
    },
    {
        value: 4,
        title: '만보기 월간',
        description: '월간 만보기 랭킹 기준으로 경품을 노출합니다.',
    },
]

const RANKING_BANNER_STATUS_OPTIONS = [
    {
        value: 1,
        title: '노출',
        description: '사용자 화면에 랭킹경품을 표시합니다.',
    },
    {
        value: 0,
        title: '미노출',
        description: '관리자에서만 보관하고 사용자 화면에는 표시하지 않습니다.',
    },
]

const getImageFileName = (imageFile) => {
    if (!imageFile || typeof imageFile !== 'object') {
        return ''
    }

    return imageFile.name || ''
}

function FormSection({ title, description, children }) {
    return (
        <Paper variant="outlined" sx={PANEL_SX}>
            <Box sx={{ mb: 1.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    {title}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                    {description}
                </Typography>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {children}
            </Box>
        </Paper>
    )
}

function ChoiceCardLabel({ title, description }) {
    return (
        <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                {title}
            </Typography>
            <Typography variant="caption" color="text.secondary">
                {description}
            </Typography>
        </Box>
    )
}

// 카드형 선택 UI에서도 기존 폼 변경 핸들러가 받던 name/value 이벤트 계약을 유지한다.
const createFieldChangeEvent = (name, value) => ({
    target: {
        name,
        value,
    },
})

function SettingChoiceGroup({
    title,
    name,
    value,
    options,
    disabled,
    onChange,
    gridTemplateColumns = { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
}) {
    return (
        <FormControl fullWidth>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                {title}
            </Typography>
            <Box
                role="radiogroup"
                aria-label={title}
                sx={{
                    display: 'grid',
                    gridTemplateColumns,
                    gap: 1,
                    mt: 1,
                }}
            >
                {options.map((option) => {
                    const checked = String(value) === String(option.value)

                    return (
                        <FormControlLabel
                            key={option.value}
                            value={option.value}
                            control={(
                                <Radio
                                    name={name}
                                    checked={checked}
                                    onChange={() => onChange(createFieldChangeEvent(name, option.value))}
                                    disabled={disabled}
                                />
                            )}
                            disabled={disabled}
                            sx={getChoiceCardSx(checked, disabled)}
                            label={(
                                <ChoiceCardLabel
                                    title={option.title}
                                    description={option.description}
                                />
                            )}
                        />
                    )
                })}
            </Box>
        </FormControl>
    )
}

function RankingBannerImageUploadPanel({
    fieldConfig,
    imageSrc,
    imageFile,
    disabled,
    onUploadClick,
}) {
    const imageFileName = getImageFileName(imageFile)

    return (
        <Paper variant="outlined" sx={PANEL_SX}>
            <Box sx={{ mb: 1.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    {fieldConfig.previewAlt}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                    App 랭킹경품 화면에 노출되는 이미지를 확인하고 교체합니다.
                </Typography>
            </Box>
            <Box sx={IMAGE_PREVIEW_FRAME_SX}>
                {imageSrc ? (
                    <Box
                        component="img"
                        src={imageSrc}
                        alt={`${fieldConfig.previewAlt} 미리보기`}
                        sx={IMAGE_PREVIEW_SX}
                    />
                ) : (
                    <Typography variant="body2" color="text.secondary">
                        이미지를 선택하면 미리보기가 표시됩니다.
                    </Typography>
                )}
            </Box>
            <FormControl fullWidth>
                <TextField
                    inputProps={{ accept: APP_RANKING_BANNER_IMAGE_FILE_ACCEPT }}
                    style={{ display: 'none' }}
                    name={fieldConfig.fieldName}
                    id={fieldConfig.inputId}
                    type="file"
                    disabled={disabled}
                    onChange={onUploadClick}
                />
                <Box component="label" htmlFor={fieldConfig.inputId} sx={{ display: 'block' }}>
                    <Button
                        fullWidth
                        variant="contained"
                        color="success"
                        component="span"
                        startIcon={<Add />}
                        sx={FORM_WIDE_UPLOAD_BUTTON_SX}
                        disabled={disabled}
                    >
                        {fieldConfig.uploadLabel}
                    </Button>
                </Box>
            </FormControl>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                {imageFileName ? `선택한 파일: ${imageFileName}` : '저장 전 미리보기로 이미지가 올바른지 확인하세요.'}
            </Typography>
        </Paper>
    )
}

export default function AppRankingBanner(props) {
    const history = useHistory()
    const location = useLocation()
    const locationState = getLocationState(location)
    const id = props.match.params.rankingBannerId
    const curPage = locationState.curPage || 1
    const searchText = locationState.searchText || ''
    const searchSelect = locationState.searchSelect || { searchType: '' }
    const listSearch = location.search || ''
    const draftKey = createEditAppRankingBannerDraftKey(id)
    const [loading, setLoading] = useState(true)
    const [hasDetail, setHasDetail] = useState(false)
    const [saving, setSaving] = useState(false)
    const [draftReady, setDraftReady] = useState(false)
    const [params, setParams] = useState(() => createDefaultAppRankingBannerParams())
    const [image, setImage] = useState('')
    const [openSnackbar, setOpenSnackbar] = useState(false)
    const [severitySnackbar, setSeveritySnackbar] = useState('info')
    const [messageSnackbar, setMessageSnackbar] = useState('')
    const [detailErrorMessage, setDetailErrorMessage] = useState('')
    const [navigationUnlocked, setNavigationUnlocked] = useState(false)
    const initialDraftSnapshotRef = useRef('')
    const leaveGuardEnabled = hasPendingAppRankingBannerImage(params) && !saving && !navigationUnlocked
    const imageFieldConfigs = APP_RANKING_BANNER_IMAGE_SECTION_CONFIGS.edit

    usePendingAppRankingBannerImageUnloadGuard(leaveGuardEnabled)

    const getData = useCallback(async () => {
        if (!id) {
            const errorMessage = '필수 파라미터가 없어 App 랭킹경품 상세를 불러올 수 없습니다.'
            setParams(createDefaultAppRankingBannerParams())
            setImage('')
            setHasDetail(false)
            setDraftReady(false)
            setDetailErrorMessage(errorMessage)
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar(errorMessage)
            setLoading(false)
            return
        }

        setLoading(true)
        setHasDetail(false)
        setDraftReady(false)
        setDetailErrorMessage('')

        try {
            const { data } = await API.post('/api/v1/admin/appRankingBanner/getDetail', { rankingBannerId: id })
            if (data.code === 200) {
                const baseParams = data.data || {}

                if (!baseParams.rankingBannerId) {
                    setParams(createDefaultAppRankingBannerParams())
                    setImage('')
                    setDetailErrorMessage('App 랭킹경품 상세 정보를 찾을 수 없습니다.')
                    setLoading(false)
                    return
                }

                const storedDraft = readAppRankingBannerDraft(draftKey)
                const restoredDraft = applyAppRankingBannerDraft({
                    baseParams,
                    draft: storedDraft,
                })

                initialDraftSnapshotRef.current = JSON.stringify(createAppRankingBannerDraftPayload({
                    params: baseParams,
                }))

                setParams(restoredDraft.params)
                setImage(baseParams.image === null ? '/images/noimage.jpg' : `${process.env.REACT_APP_AWS_S3_URL}/${baseParams.image}`)
                setHasDetail(true)
                setDetailErrorMessage('')
                setDraftReady(true)
            } else if (data.code === 403) {
                history.push('/login')
                return
            } else {
                const errorMessage = data.message || 'App 랭킹경품 상세를 불러오지 못했습니다.'
                setParams(createDefaultAppRankingBannerParams())
                setImage('')
                setDetailErrorMessage(errorMessage)
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(errorMessage)
            }
        } catch (_error) {
            const errorMessage = 'App 랭킹경품 상세를 불러오지 못했습니다.'
            setParams(createDefaultAppRankingBannerParams())
            setImage('')
            setDetailErrorMessage(errorMessage)
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar(errorMessage)
        } finally {
            setLoading(false)
        }
    }, [draftKey, history, id])

    useEffect(() => {
        getData()
    }, [getData])

    useEffect(() => {
        if (!draftReady || !hasDetail) {
            return
        }

        // App 랭킹경품 수정은 목록 query를 건드리지 않고 폼 draft만 sessionStorage에 보존한다.
        const nextDraft = createAppRankingBannerDraftPayload({
            params,
        })
        const nextDraftSnapshot = JSON.stringify(nextDraft)

        if (nextDraftSnapshot === initialDraftSnapshotRef.current) {
            clearAppRankingBannerDraft(draftKey)
            return
        }

        saveAppRankingBannerDraft(draftKey, nextDraft)
    }, [draftKey, draftReady, hasDetail, params])

    const handleClose = () => {
        setOpenSnackbar(false)
    }

    const moveToList = (snackbarState) => {
        moveToAppRankingBannerList({
            draftKey,
            clearDraft: clearAppRankingBannerDraft,
            setNavigationUnlocked,
            history,
            listSearch,
            snackbarState,
            curPage,
            searchText,
            searchSelect,
        })
    }

    const handleBackClick = (event) => {
        handleAppRankingBannerBackNavigation({
            event,
            leaveGuardEnabled,
            draftKey,
            clearDraft: clearAppRankingBannerDraft,
            confirmLeave: () => window.confirm(APP_RANKING_BANNER_IMAGE_LEAVE_MESSAGE),
            moveToList,
        })
    }

    const handleUpdateField = (e) => {
        setParams((prev) => applyAppRankingBannerFieldChange({
            params: prev,
            name: e.target.name,
            value: e.target.value,
        }))
    }

    const handleUploadClick = (e) => {
        handleAppRankingBannerImageUpload({
            event: e,
            setParams,
            setImage,
            onInvalidFile: (message = APP_RANKING_BANNER_IMAGE_FILE_ERROR_MESSAGE) => {
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(message)
            },
        })
    }

    const updateData = async (formData) => {
        try {
            setSaving(true)

            const { data } = await API({
                method: 'post',
                url: '/api/v1/admin/appRankingBanner/update',
                data: formData,
                headers: { "Content-Type": "multipart/form-data" }
            })

            if (data.code === 200) {
                // 저장 후에도 운영자가 보던 App 랭킹경품 목록 조건으로 그대로 복귀시킨다.
                moveToList({
                    severity: 'success',
                    message: data.message,
                })
            } else if (data.code === 403) {
                history.push('/login')
            } else {
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(data.message)
            }
        } catch (_error) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('App 랭킹경품을 저장하지 못했습니다.')
        } finally {
            setSaving(false)
        }
    }

    const handleSubmit = (e) => {
        e.preventDefault()

        if (saving) {
            return
        }

        const { nextParams, errorMessage, formData } = prepareAppRankingBannerSubmit({
            params,
            rankingBannerId: id,
            titleRequiredMessage: '제목을 입력하세요.',
        })

        setParams(nextParams)

        if (errorMessage) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar(errorMessage)
            return
        }

        updateData(formData)
    }

    // 상세 조회 실패 시 목록 화면과 같은 오류 빈 상태 안내를 사용한다.
    const renderDetailErrorState = () => (
        <ListEmptyState
            variant="error"
            title="App 랭킹경품 상세를 불러오지 못했습니다."
            description={detailErrorMessage || '목록으로 돌아가 다시 확인하세요.'}
            action={(
                <BottomButtonGroup variant="emptyStateAction" path="/appRankingBanners" search={listSearch} visible={false} curPage={curPage} searchText={searchText} searchSelect={searchSelect} onBackClick={() => clearAppRankingBannerDraft(draftKey)} backLabel="목록으로"/>
            )}
            sx={{ mt: 2 }}
        />
    )

    if (loading) {
        return (
            <Grid item xs={12}>
                <Spiner/>
                {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>}
            </Grid>
        )
    }

    if (!hasDetail) {
        return (
            <Grid item xs={12}>
                <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
                    <Title>App 랭킹경품 수정</Title>
                    {renderDetailErrorState()}
                    {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>}
                </Paper>
            </Grid>
        )
    }

    return (
        <Grid item xs={12}>
            <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
                <Prompt when={leaveGuardEnabled} message={APP_RANKING_BANNER_IMAGE_LEAVE_MESSAGE} />
                <Title>App 랭킹경품 수정</Title>
                <Box component="form" encType="multipart/form-data" onSubmit={handleSubmit} sx={{ opacity: saving ? 0.6 : 1 }}>
                    <Grid container spacing={2} sx={{ marginTop: 2 }}>
                        {imageFieldConfigs.map((fieldConfig) => (
                            <Grid key={fieldConfig.inputId} item xs={12} md={5}>
                                <RankingBannerImageUploadPanel
                                    fieldConfig={fieldConfig}
                                    imageSrc={image}
                                    imageFile={params[fieldConfig.fieldName]}
                                    disabled={saving}
                                    onUploadClick={handleUploadClick}
                                />
                            </Grid>
                        ))}
                        <Grid item xs={12} md={7}>
                            <FormSection
                                title="경품 정보"
                                description="랭킹경품의 순위, 제목, 이동 링크를 수정합니다."
                            >
                                <FormControl fullWidth>
                                    <TextField
                                        required
                                        label="시작 순위"
                                        id="rank"
                                        name="rank"
                                        value={params.rank ?? ''}
                                        disabled={saving}
                                        onChange={handleUpdateField}
                                        size="small"
                                    />
                                </FormControl>
                                <FormControl fullWidth>
                                    <TextField
                                        required
                                        label="제목"
                                        id="title"
                                        name="title"
                                        value={params.title || ''}
                                        disabled={saving}
                                        onChange={handleUpdateField}
                                        size="small"
                                    />
                                </FormControl>
                                <FormControl fullWidth>
                                    <TextField
                                        label="링크 URL"
                                        id="url"
                                        name="url"
                                        value={params.url || ''}
                                        disabled={saving}
                                        onChange={handleUpdateField}
                                        size="small"
                                    />
                                </FormControl>
                            </FormSection>
                        </Grid>
                        <Grid item xs={12}>
                            <FormSection
                                title="경품 지급"
                                description={params.isApplyingNow
                                    ? '지금 기간에 적용되고 있는 규칙입니다. 수정하면 다음 지급에 반영됩니다.'
                                    : '포인트·기프티콘 지급 규칙과 적용 기간을 설정합니다.'}
                            >
                                <AppRankingBannerRewardFields
                                    params={params}
                                    disabled={saving}
                                    onChange={handleUpdateField}
                                    goodsName={params.goodsName || ''}
                                />
                            </FormSection>
                        </Grid>
                        <Grid item xs={12}>
                            <FormSection
                                title="노출 설정"
                                description="랭킹 기준과 사용자 화면 노출 여부를 함께 확인합니다."
                            >
                                <SettingChoiceGroup
                                    title="구분"
                                    name="type"
                                    value={params.type || 1}
                                    options={RANKING_BANNER_TYPE_OPTIONS}
                                    disabled={saving}
                                    onChange={handleUpdateField}
                                    gridTemplateColumns={{ xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', md: 'repeat(4, minmax(0, 1fr))' }}
                                />
                                <SettingChoiceGroup
                                    title="노출 여부"
                                    name="status"
                                    value={params.status ?? 1}
                                    options={RANKING_BANNER_STATUS_OPTIONS}
                                    disabled={saving}
                                    onChange={handleUpdateField}
                                />
                                <SettingChoiceGroup
                                    title="노출 국가"
                                    name="exposeRegion"
                                    value={params.exposeRegion || APP_RANKING_BANNER_EXPOSE_REGIONS.ALL}
                                    options={APP_RANKING_BANNER_EXPOSE_REGION_OPTIONS}
                                    disabled={saving}
                                    onChange={handleUpdateField}
                                    gridTemplateColumns={{ xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))' }}
                                />
                            </FormSection>
                        </Grid>
                    </Grid>
                    <BottomButtonGroup path="/appRankingBanners" search={listSearch} visible={true} curPage={curPage} searchText={searchText} searchSelect={searchSelect} submitDisabled={saving} submitLabel={saving ? '저장 중...' : '저장'} onBackClick={handleBackClick}/>
                </Box>
                {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>}
            </Paper>
        </Grid>
    )
}
